import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import moment from 'moment-timezone';
import Select, { components } from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import { API_URL } from '../../../config/constants';
import './Monitoring.scss';
import CustomLineChart from '../../components/CustomLineChart';
import DataListMenu from './DataListMenu';
import { idKey, resolveAnchorRowIndex } from './selectionUtils';
import csvSaveIcon from '../../../images/csvSave.png';
import filledArrowIconImg from '../../../images/filledArrowIcon.png';
import resetIconImg from '../../../images/resetIcon.png';
import refreshIcon from '../../../images/refresh.png';
import { CustomDatePickerStart, CustomDatePickerEnd } from '../../components/CustomDatePicker';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import Loading from '../../components/Loading';
import NoData from '../../components/NoData';
import ChkDelModal from '../../components/ChkDelModal';
import FadeOutNoti from '../../components/FadeOutNoti';
import { useSelector } from 'react-redux';
import { getModelTypes, getModelNames, getModelNos, getCurrentProcessStartedAt } from '../../../api/modelApi';
import JSZip from "jszip";
import { saveAs } from "file-saver";

/** 라디오 표시: DetailedDataStruct.class(원자재/하중/치수) 우선, 없으면 tableName */
function getSensorRadioLabel(item) {
    if (!item) return '';
    return item.class || item.tableName || '';
}

const OPTION_ALL = { value: '', label: '전체 선택' };

function parseQueryDate(value, fallback) {
    if (!value) return fallback;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? fallback : parsed;
}

function RealTimeMonitoring(props) {
    // 현재 로그인한 유저가 일반 사용자인지 여부
    const { privilege } = useSelector((state) => state.session);
    const {  selSumDsId } = useOutletContext();
    const navigate = useNavigate();
    const location = useLocation();

    // 공정 요약에서 '상세' 버튼으로 진입했을 경우 데이터 요청에 활용되는 쿼리스트링
    const [searchParams, setSearchParams] = useSearchParams();
    const qsSdsId = searchParams.get("sdsId");
    const qsSIdx = searchParams.get("sIdx");
    const qsDataId = searchParams.get("dataId");
    const qsSumDataId = searchParams.get("sumDataId");
    const qsFilterStartDate = searchParams.get("filterStartDate");
    const qsFilterEndDate = searchParams.get("filterEndDate");
    const hasQsFilterTime =
        qsFilterStartDate != null &&
        qsFilterEndDate != null &&
        !Number.isNaN(new Date(qsFilterStartDate).getTime()) &&
        !Number.isNaN(new Date(qsFilterEndDate).getTime());
    const {userRank} = useSelector((state) => state.session);

    const [isPending, startTransition] = useTransition();

    // CSV 저장 시 파일 명으로 활용할 접두어
    const companyInitial = 'K';
    const sensorTypeInitial = 'D';
    // 추후 계정 정보에서 company 컬럼 데이터 받아오도록 수정要
    // company 정보를 기준으로 rawData 테이블을 선별해 받아옴
    const company = "세동산업";

    const [dataStructures, setDataStructures] = useState([]);   // 라디오 버튼 및 각종 세부 데이터 구조 관련 정보
    const [curPageData, setCurPageData] = useState({}); // 현재 세부데이터 영역에서 출력하고 있는 데이터
    /** Shift 범위 선택 기준점 — 화면상 자동 선택과 반드시 동기화 (탭 전환 후에도 즉시 Shift 가능) */
    const lastClickedAnchorRef = useRef(null);
    const preserveSumDataIdRef = useRef(null);
    const sensorSumDataIdRef = useRef(null);
    const pendingMatchRef = useRef({
        sumDataId : null,
        remainingLoads : 0,
    })
    const [listScrollRequest, setListScrollRequest] = useState({
        token : 0,
        mode : 'top',
    })

    const [lastId, setLastId] = useState(null); // getMoreData에서 불러올 세부데이터 시작점을 전달하기 위한 마지막 세부데이터 id
    const [noMoreData, setNoMoreData] = useState(false);    // 더이상 DB에 getMoreData로 불러올 과거 세부데이터가 없을 때 true

    const [dataList, setDataList] = useState([]); // 세부데이터 목록
    const md = dataList || [];
    const [selSensorIdx, setSelSensorIdx] = useState(-1);   // 상단 라디오 버튼 선택 상태
    const [selDataIdx, setSelDataIdx] = useState(-1);   // 좌측 세부데이터 목록 선택 상태
    const prevSelSumDsIdRef = useRef(undefined);
    const syncedSumKeyRef = useRef(null);
    const [chkedData, setChkedData] = useState([]);
    const [lastSumDataId, setLastSumDataId] = useState(null);

    // 수정모드 관련 상태
    const [formData, setFormData] = useState([]);

    const [chkDelModalIsOpen, setChkDelModalIsOpen] = useState(false);  // 삭제 여부 재확인 팝업모달 출력 여부
    const [isLoading, setIsLoading] = useState(true);   //  API서버에서 데이터 요청 중일 때 대신 출력할 로딩 페이지 출력 여부
    const [pageData, setPageData] = useState(true);     // 페이지 데이터 없을 경우 로딩 페이지로 안넘어가고 NoData 페이지 출력 여부

    const [useQs, setUseQs] = useState(!!qsSdsId);
    const [toast, setToast] = useState({
        open: false,
        message: '',
        nextDataId: null,
    });

    /** 통계(품질 이력) 페이지와 동일한 기간·품목 필터 */
    const [filterStartDate, setFilterStartDate] = useState(() =>
        parseQueryDate(qsFilterStartDate, moment().startOf('day').toDate())
    );
    const [filterEndDate, setFilterEndDate] = useState(() =>
        parseQueryDate(qsFilterEndDate, moment().toDate())
    );
    const [selectPeriod, setSelectPeriod] = useState(hasQsFilterTime ? 'input' : 'currentWorks');
    const [currentProcessStartedAt, setCurrentProcessStartedAt] = useState(null);
    const [currentProcessStartedLoaded, setCurrentProcessStartedLoaded] = useState(hasQsFilterTime);
    const [currentProcessStartedSumDsId, setCurrentProcessStartedSumDsId] = useState(null);
    const currentWorksAppliedKeyRef = useRef(null);
    const skipNextCurrentWorksApplyRef = useRef(false);
    const [modelTypes, setModelTypes] = useState([]);
    const [modelNames, setModelNames] = useState([]);
    const [modelNos, setModelNos] = useState([]);
    const [selectedModelType, setSelectedModelType] = useState(OPTION_ALL);
    const [selectedModelName, setSelectedModelName] = useState(OPTION_ALL);
    const [selectedModelNo, setSelectedModelNo] = useState(OPTION_ALL);
    /** 세부데이터 목록 API: true일 때만 백엔드에서 summ_data 매칭 조회 (적용 버튼으로만 true) */
    const [monitFilterApplied, setMonitFilterApplied] = useState(hasQsFilterTime);

    /** 적용 버튼으로 확정된 필터(탭 전환 시 유지). UI draft와 분리 */
    const [appliedStartDate, setAppliedStartDate] = useState(() =>
        parseQueryDate(qsFilterStartDate, moment().startOf('day').toDate())
    );
    const [appliedEndDate, setAppliedEndDate] = useState(() =>
        parseQueryDate(qsFilterEndDate, moment().toDate())
    );
    const [appliedModelType, setAppliedModelType] = useState(OPTION_ALL);
    const [appliedModelName, setAppliedModelName] = useState(OPTION_ALL);
    const [appliedModelNo, setAppliedModelNo] = useState(OPTION_ALL);

    const periodOpt = [
        { label: '시간직접입력', value: 'input' },
        { label: '현재공정', value: 'currentWorks' },
        { label: '최근 1시간', value: '1h' },
        { label: '최근 24시간', value: '24h' },
    ];

    function syncShiftAnchorFromRow(row) {
        if (!row) {
            lastClickedAnchorRef.current = null;
            return;
        }
        lastClickedAnchorRef.current = {
            sumDataId: row.sumDataId ?? null,
            dataId: row.dataId,
        };
        if (row.sumDataId != null && row.sumDataId !== '') {
            sensorSumDataIdRef.current = row.sumDataId;
        }
    }

    function isSameSumDataId(a,b) {
        if (a==null || b==null) return false;
        return String(a) === String(b);
    }

    function shiftAnchorIsSet() {
        const a = lastClickedAnchorRef.current;
        if (!a) return false;
        return (
            (a.sumDataId != null && a.sumDataId !== '') ||
            (a.dataId != null && a.dataId !== '')
        );
    }

    const handleRowClick = (e, idx, dataId) => {
        pendingMatchRef.current = { sumDataId: null, remainingLoads: 0 };
        const list = dataList ?? [];

        // Shift: 앵커(마지막 클릭·또는 자동 선택과 동기화된 행)~현재 행 범위를 Export 선택에 합침
        if (e.shiftKey && shiftAnchorIsSet()) {
            const anchorIdx = resolveAnchorRowIndex(list, lastClickedAnchorRef.current);
            const ai = anchorIdx != null ? anchorIdx : 0;
            const start = Math.min(ai, idx);
            const end = Math.max(ai, idx);
            const range = list.slice(start, end + 1).map((d) => d.dataId);
            setChkedData((prev) => {
                const seen = new Set(prev.map(idKey));
                const out = [...prev];
                for (const id of range) {
                    const k = idKey(id);
                    if (!seen.has(k)) {
                        seen.add(k);
                        out.push(id);
                    }
                }
                return out;
            });
            setSelDataIdx(idx);
            setListScrollRequest((prev) => ({
                token: prev.token + 1,
                mode : 'user-click'
            }));
        }
        // Ctrl/⌘: 해당 행만 Export 목록에 넣거나 빼기 (개별 토글)
        else if (e.ctrlKey || e.metaKey) {
            const k = idKey(dataId);
            const prev = chkedData;
            const has = prev.some((id) => idKey(id) === k);
            const next = has ? prev.filter((id) => idKey(id) !== k) : [...prev, dataId];
            setChkedData(next);
            if (has && selDataIdx === idx) {
                const firstIdx = list.findIndex((d) =>
                    next.some((id) => idKey(id) === idKey(d.dataId))
                );
                setSelDataIdx(firstIdx >= 0 ? firstIdx : -1);
            } else {
                setSelDataIdx(idx);
            }
            setListScrollRequest((prevReq) => ({
                token : prevReq.token + 1,
                mode : 'user-click'
            }))
        }
        // 일반 클릭: 단일 선택 + 상세 보기 인덱스
        else {
            setChkedData([dataId]);
            setSelDataIdx(idx);
            setListScrollRequest((prev) => ({
                token: prev.token + 1,
                mode: 'user-click',
            }));
        }

        const row = list[idx];
        syncShiftAnchorFromRow(row ?? { dataId, sumDataId: null });
    };

    const effSumDsId = useMemo(() => {
        if (qsSdsId != null && qsSdsId !== '') return Number(qsSdsId);
        if (selSensorIdx >= 0 && dataStructures[selSensorIdx]?.sumDsId != null) {
            return Number(dataStructures[selSensorIdx].sumDsId);
        }
        return null;
    }, [qsSdsId, selSensorIdx, dataStructures]);
    const getCurrentWorksStartDate = useCallback(() => (
        currentProcessStartedAt ? moment(currentProcessStartedAt).toDate() : moment().startOf('day').toDate()
    ), [currentProcessStartedAt]);
    const isCurrentProcessStartReady =
        selectPeriod !== 'currentWorks' ||
        hasQsFilterTime ||
        (
            currentProcessStartedLoaded &&
            effSumDsId != null &&
            !Number.isNaN(effSumDsId) &&
            Number(currentProcessStartedSumDsId) === Number(effSumDsId)
        );

    useEffect(() => {
        if (effSumDsId == null || Number.isNaN(effSumDsId)) {
            setCurrentProcessStartedAt(null);
            setCurrentProcessStartedLoaded(false);
            setCurrentProcessStartedSumDsId(null);
            return;
        }
        let mounted = true;
        setCurrentProcessStartedLoaded(false);
        setCurrentProcessStartedSumDsId(null);
        getCurrentProcessStartedAt({ sumDsId: effSumDsId })
            .then((startedAt) => {
                if (mounted) {
                    setCurrentProcessStartedAt(startedAt);
                    setCurrentProcessStartedLoaded(true);
                    setCurrentProcessStartedSumDsId(effSumDsId);
                }
            })
            .catch((err) => {
                console.error('getCurrentProcessStartedAt failed:', err);
                if (mounted) {
                    setCurrentProcessStartedAt(null);
                    setCurrentProcessStartedLoaded(true);
                    setCurrentProcessStartedSumDsId(effSumDsId);
                }
            });
        return () => { mounted = false; };
    }, [effSumDsId]);
    const modelTypeOptions = useMemo(
        () => [OPTION_ALL, ...modelTypes.map((t) => (typeof t === 'string' ? { value: t, label: t } : t))],
        [modelTypes]
    );
    const modelNameOptions = useMemo(() => {
        if (!selectedModelType || selectedModelType.value === '') return [OPTION_ALL];
        return [OPTION_ALL, ...modelNames.map((n) => (typeof n === 'string' ? { value: n, label: n } : n))];
    }, [modelNames, selectedModelType]);
    const modelNoOptions = useMemo(() => {
        if (!selectedModelName || selectedModelName.value === '') return [OPTION_ALL];
        return [OPTION_ALL, ...modelNos.map((no) => (typeof no === 'string' ? { value: no, label: no } : no))];
    }, [modelNos, selectedModelName]);

    useEffect(() => {
        switch (selectPeriod) {
            case 'input':
                break;
            case 'currentWorks':
                if (!isCurrentProcessStartReady) break;
                setFilterStartDate(getCurrentWorksStartDate());
                setFilterEndDate(moment().toDate());
                break;
            case '1h':
                setFilterStartDate(moment().subtract(1, 'hours').toDate());
                setFilterEndDate(moment().toDate());
                break;
            case '24h':
                setFilterStartDate(moment().subtract(1, 'days').toDate());
                setFilterEndDate(moment().toDate());
                break;
            default:
                setFilterStartDate(moment().toDate());
                setFilterEndDate(moment().toDate());
                break;
        }
    }, [selectPeriod, isCurrentProcessStartReady, getCurrentWorksStartDate]);

    useEffect(() => {
        if (effSumDsId == null || Number.isNaN(effSumDsId)) {
            setModelTypes([]);
            return;
        }
        getModelTypes({ sumDsId: effSumDsId })
            .then((data) => {
                const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
                setModelTypes(opts);
            })
            .catch(() => setModelTypes([]));
    }, [effSumDsId]);

    useEffect(() => {
        if (effSumDsId == null || !selectedModelType || selectedModelType.value === '') {
            setModelNames([]);
            return;
        }
        getModelNames({ sumDsId: effSumDsId, modelType: selectedModelType })
            .then((data) => {
                const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
                setModelNames(opts);
            })
            .catch(() => setModelNames([]));
    }, [effSumDsId, selectedModelType]);

    useEffect(() => {
        if (effSumDsId == null || !selectedModelName || selectedModelName.value === '') {
            setModelNos([]);
            return;
        }
        getModelNos({ sumDsId: effSumDsId, modelType: selectedModelType, modelName: selectedModelName })
            .then((data) => {
                const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
                setModelNos(opts);
            })
            .catch(() => setModelNos([]));
    }, [effSumDsId, selectedModelType, selectedModelName]);

    //좌측 데이터 선택(row) 변경 시, 선택된 데이터의 모델 메타를 상단 필터 UI와 동기화
    // useEffect(() => {
    //     if (selDataIdx < 0 || Object.keys(curPageData).length === 0) return;

    //     const selectedMonit = curPageData?.monitData;
    //     const sumDataId = selectedMonit?.sumDataId;
    //     const currentSumDsId = curPageData?.dataStructure?.sumDsId;
    //     if (!sumDataId || !currentSumDsId) return;
    //     const nextSyncKey = `${currentSumDsId}:${sumDataId}`;
    //     if (syncedSumKeyRef.current === nextSyncKey) return;
    //     syncedSumKeyRef.current = nextSyncKey;

    //     let cancelled = false;
    //     async function syncModelFilterFromSelectedRow() {
    //         try {
    //             const response = await axios.post(`${API_URL}/monitoring-manage/getSumData`, {
    //                 sumDsId: currentSumDsId,
    //                 dataId: sumDataId,
    //             });
    //             console.log("response", response)
    //             if (cancelled) return;
    //             const model = response?.data?.data?.model;
    //             if (!model) return;

    //             const nextType =
    //                 model.modelType != null && String(model.modelType).trim() !== ''
    //                     ? { value: String(model.modelType), label: String(model.modelType) }
    //                     : OPTION_ALL;
    //             const nextName =
    //                 model.modelName != null && String(model.modelName).trim() !== ''
    //                     ? { value: String(model.modelName), label: String(model.modelName) }
    //                     : OPTION_ALL;
    //             const nextNo =
    //                 model.modelNo != null && String(model.modelNo).trim() !== ''
    //                     ? { value: String(model.modelNo), label: String(model.modelNo) }
    //                     : OPTION_ALL;

    //             setSelectedModelType((prev) =>
    //                 prev?.value === nextType.value ? prev : nextType
    //             );
    //             setSelectedModelName((prev) =>
    //                 prev?.value === nextName.value ? prev : nextName
    //             );
    //             setSelectedModelNo((prev) =>
    //                 prev?.value === nextNo.value ? prev : nextNo
    //             );
    //         } catch (err) {
    //             console.error(err);
    //         }
    //     }

    //     syncModelFilterFromSelectedRow();
    //     return () => {
    //         cancelled = true;
    //     };
    // }, [selDataIdx, curPageData?.monitData?.sumDataId, curPageData?.dataStructure?.sumDsId]);

    function buildMonitFilterBody() {
        const start = monitFilterApplied ? appliedStartDate : filterStartDate;
        const end = monitFilterApplied ? appliedEndDate : filterEndDate;
        const mt = monitFilterApplied ? appliedModelType : selectedModelType;
        const mn = monitFilterApplied ? appliedModelName : selectedModelName;
        const mo = monitFilterApplied ? appliedModelNo : selectedModelNo;
        return {
            filter_time: {
                fixedStartDate: start,
                fixedEndDate: end,
            },
            filter_modelType: !mt || mt.value === '' ? 'all' : String(mt.value),
            filter_modelName: !mn || mn.value === '' ? 'all' : String(mn.value),
            filter_modelNo: !mo || mo.value === '' ? 'all' : String(mo.value),
            isFilterApplied: monitFilterApplied === true,
        };
    }

    async function getDds() {
        // 페이지가 처음 마운트될 때 데이터 구조 가져옴
        await axios.post(`${API_URL}/monitoring-manage/getMonitDataStructures`, { company: '세동산업', sdsId: qsSdsId || selSumDsId, dataId: qsDataId || null }).then((res) => {
            const IndexDims = res.data['dataStructures'].map(item => item.indexDim);
            console.log("으아악",IndexDims)
            const dataStructureArr = res.data["dataStructures"].filter(item => {
                const valView = item["valView"];
                return !(valView.filter(el => el === true).length === 0)
            });
            console.log('dataSTructureArrr', dataStructureArr)
            if (dataStructureArr.length === 0) {
                setIsLoading(false);
                setPageData(false);
                return;
            }
            if (dataStructureArr.length > 0) {
                if (qsSIdx) {
                    setSelSensorIdx(parseInt(qsSIdx));
                } else {
                    setSelSensorIdx(0);
                }
                setDataStructures(dataStructureArr)
                console.log("dataStructureArr", dataStructureArr)
            }
        })
    }

    /**
     * @param {{ fixedStartDate?: Date; fixedEndDate?: Date; forceUseQs?: boolean; sensorIdxOverride?: number; isFilterApplied?: boolean }} [options]
     * isFilterApplied: true → 이번 요청만 확정(적용 직전, 아직 setState 반영 전일 때 draft 모델 사용)
     */
    async function getInitMonit(options = {}) {
        const { fixedStartDate, fixedEndDate, forceUseQs, sensorIdxOverride, isFilterApplied: isFilterAppliedOpt } = options;
        const reqSensorIdx = Number.isInteger(sensorIdxOverride) ? sensorIdxOverride : selSensorIdx;
        const shouldUseQs = typeof forceUseQs === 'boolean' ? forceUseQs : useQs;
        const monitSumDsId = 
            reqSensorIdx >= 0 && dataStructures[reqSensorIdx]?.sumDsId != null
                ? dataStructures[reqSensorIdx].sumDsId
                : effSumDsId;

        const requestFilterApplied =
            isFilterAppliedOpt === true || (isFilterAppliedOpt === undefined && monitFilterApplied);

        let resolvedStart;
        let resolvedEnd;
        if (fixedStartDate != null && fixedEndDate != null) {
            resolvedStart = fixedStartDate;
            resolvedEnd = fixedEndDate;
        } else if (requestFilterApplied) {
            resolvedStart = appliedStartDate;
            resolvedEnd = appliedEndDate;
        } else {
            resolvedStart = filterStartDate;
            resolvedEnd = filterEndDate;
        }

        const filter_time = {
            fixedStartDate: resolvedStart,
            fixedEndDate: resolvedEnd,
        };

        const useDraftModelsForThisRequest = isFilterAppliedOpt === true;
        const srcType = useDraftModelsForThisRequest
            ? selectedModelType
            : requestFilterApplied
              ? appliedModelType
              : selectedModelType;
        const srcName = useDraftModelsForThisRequest
            ? selectedModelName
            : requestFilterApplied
              ? appliedModelName
              : selectedModelName;
        const srcNo = useDraftModelsForThisRequest
            ? selectedModelNo
            : requestFilterApplied
              ? appliedModelNo
              : selectedModelNo;

        const filterBody =
            monitSumDsId != null
                ? {
                      monitSumDsId,
                      filter_time,
                      filter_modelType: !srcType || srcType.value === '' ? 'all' : String(srcType.value),
                      filter_modelName: !srcName || srcName.value === '' ? 'all' : String(srcName.value),
                      filter_modelNo: !srcNo || srcNo.value === '' ? 'all' : String(srcNo.value),
                  }
                : {};
        console.log('[monit-ui] getInitMonit payload summary', {
            shouldUseQs,
            reqSensorIdx,
            selSensorIdx,
            dataStructuresLength: Array.isArray(dataStructures) ? dataStructures.length : 'not-array',
            firstTableNames: Array.isArray(dataStructures)
                ? dataStructures.slice(0, 5).map((d) => d?.tableName)
                : [],
            sdsId: shouldUseQs ? qsSdsId : selSumDsId,
            sIdx: shouldUseQs ? qsSIdx : null,
            dataId: shouldUseQs ? qsDataId : null,
            monitSumDsId,
        });
        await axios
            .post(`${API_URL}/monitoring-manage/getInitMonit`, {
                company: '세동산업',
                sdsId: shouldUseQs ? qsSdsId : selSumDsId,
                sIdx: shouldUseQs ? qsSIdx : null,
                dataId: shouldUseQs ? qsDataId : null,
                // URL의 dataId는 정확한 복귀 대상이다. 이때 sumDataId까지 보내면
                // API가 같은 카운트의 최신 raw dataId로 대체하므로 센서 전환 때만 앵커를 쓴다.
                anchorSumDataId: shouldUseQs ? null : preserveSumDataIdRef.current,
                dataStructures,
                selSensorIdx: reqSensorIdx,
                isFilterApplied: requestFilterApplied,
                ...filterBody,
            })
            .then((res) => {
                const resInitData = res.data;
                const md = resInitData['datas']['monitData'] || [];
                const lastMdId = md.length > 0 ? md[md.length - 1].dataId : -1;
                const nextLastSumDataId = resInitData['datas']['lastSumDataId'] ?? null;

                const preservedSumDataId = preserveSumDataIdRef.current;
                const preservedMatchIdx =
                    !shouldUseQs && preservedSumDataId != null && md.length > 0
                        ? md.findIndex((row) => isSameSumDataId(row?.sumDataId, preservedSumDataId))
                        : -1;
                const hasMissingPreservedMatch =
                    !shouldUseQs && preservedSumDataId != null && preservedMatchIdx < 0;

                if (preservedMatchIdx >= 0){
                    pendingMatchRef.current = {
                        sumDataId : null,
                        remainingLoads : 0,
                    }
                }
                // 1회성 적용 (센서 전환 직후에만)
                preserveSumDataIdRef.current = null;
                if (resInitData['selectedInfo'] === undefined) {
                    if (hasMissingPreservedMatch) {
                        pendingMatchRef.current = { sumDataId: null, remainingLoads: 0 };
                        startTransition(() => setSelDataIdx(-1));
                        setChkedData([]);
                        lastClickedAnchorRef.current = null;
                        setCurPageData(false);
                        setFormData(false);
                    } else if (md.length > 0) {
                        const nextIdx = preservedMatchIdx >= 0 ? preservedMatchIdx : 0;
                        startTransition(() => setSelDataIdx(nextIdx));
                        setChkedData([md[nextIdx].dataId]);
                        syncShiftAnchorFromRow(md[nextIdx]);

                        setListScrollRequest((prev) => ({
                            token: prev.token + 1,
                            mode: preservedMatchIdx >= 0 ? 'auto-match' : 'top',
                        }));
                    } else {
                        startTransition(() => setSelDataIdx(-1));
                        setChkedData([]);
                        lastClickedAnchorRef.current = null;
                    }
                    setLastId(() => lastMdId);
                    setLastSumDataId(() => nextLastSumDataId);
                    setDataList(() => [...md]);
                    setNoMoreData(resInitData['datas'].end);
                    setIsLoading(false);
                } else {
                    // 쿼리스트링으로 들어온 경우에만 URL의 sIdx로 맞춤. 라디오 전환 시 useQs=false인데도
                    // 백엔드가 selectedInfo를 주면 qsSIdx가 비어 있어 parse 실패 → 항상 0(원자재)로 떨어지던 버그 방지.
                    if (shouldUseQs && qsSIdx != null && qsSIdx !== '') {
                        const parsedSIdx = Number.parseInt(String(qsSIdx), 10);
                        setSelSensorIdx(Number.isInteger(parsedSIdx) && parsedSIdx >= 0 ? parsedSIdx : 0);
                    } else {
                        const keepIdx =
                            Number.isInteger(reqSensorIdx) && reqSensorIdx >= 0 ? reqSensorIdx : 0;
                        setSelSensorIdx(keepIdx);
                    }
                    const selectedDataIdx = resInitData['selectedInfo']['selectedDataIdx'];
                    const hasMissingRequestedMatch =
                        shouldUseQs && qsDataId != null && qsDataId !== '' && selectedDataIdx === -1;
                    if (hasMissingRequestedMatch && qsSumDataId != null && qsSumDataId !== '') {
                        sensorSumDataIdRef.current = qsSumDataId;
                    }
                    let nextDataIdx =
                        selectedDataIdx === -1
                            ? 0
                            : selectedDataIdx;
                    if (md.length === 0 || hasMissingPreservedMatch || hasMissingRequestedMatch) {
                        pendingMatchRef.current = { sumDataId: null, remainingLoads: 0 };
                        setSelDataIdx(-1);
                        setChkedData([]);
                        lastClickedAnchorRef.current = null;
                        setCurPageData(false);
                        setFormData(false);

                        setListScrollRequest((prev) => ({
                            token: prev.token + 1,
                            mode: 'top',
                        }));
                    } else {
                        nextDataIdx = Math.min(Math.max(0, nextDataIdx), md.length - 1);
                        if (preservedMatchIdx >= 0) nextDataIdx = preservedMatchIdx;
                        setSelDataIdx(nextDataIdx);
                        setChkedData([md[nextDataIdx].dataId]);
                        syncShiftAnchorFromRow(md[nextDataIdx]);

                        setListScrollRequest((prev) => ({
                            token: prev.token + 1,
                            mode:
                                shouldUseQs && nextDataIdx >= 0
                                    ? 'detail-match'
                                    : preservedMatchIdx >= 0
                                      ? 'auto-match'
                                      : 'top',
                        }));
                    }
                    setDataList(() => [...md]);
                    setLastId(() => (md.length > 0 ? md[md.length - 1].dataId : -1));
                    setLastSumDataId(() => (resInitData['datas']['lastSumDataId'] ?? null));
                    setNoMoreData(resInitData['datas'].end);
                    setIsLoading(false);
                }
            }).catch(err => console.error(err))
            .finally(() => {
                setIsLoading(false);
            });
    }

    async function getMonitData() {
        if (selSensorIdx < 0 || selDataIdx < 0 || !dataList[selDataIdx]) {
            setCurPageData(false);
            setFormData(false);
            return;
        }
        await axios.post(`${API_URL}/monitoring-manage/getMonitData`, { dDsId: dataStructures[selSensorIdx]["dDsId"], dataId: dataList[selDataIdx]["dataId"] }).then(res => {
            const { data } = res;
            const dsData = dataStructures[selSensorIdx];
            const monData = data.monitData;
            if (!monData || !monData.data) {
                setCurPageData(false);
                setFormData(false);
                return;
            }
            // const convMonData = monData.map(mon => {
            //     const dataStr = mon.data; // '...; 0.0, 0.0221; 0.1, 0.2311;' 형태의 센서 데이터
            //     const dataArr = dataStr.split(';');
            //     dataArr.pop();
            //     const arrayConvertedDataArr = dataArr.map(el => el.split(','));
            //     return ({ ...mon, data: arrayConvertedDataArr })
            // });
            const dataStr = monData.data; // '...; 0.0, 0.0221; 0.1, 0.2311;' 형태의 센서 데이터
            const dataArr = dataStr.split(';');
            dataArr.pop();
            const arrayConvertedDataArr = dataArr.map(el => el.split(','));
            const convMonData = {
                ...monData,
                data: arrayConvertedDataArr
            }
            const grpNameArr = dsData.indexName;
            const dimensionArr = dsData.indexDim;
            const sumDsId = dsData.sumDsId;
            const dataObj = {
                ...data,
                dataStructure: dsData,
                monitData: convMonData,
                rawNo: dsData.rawNo,
                indexName: grpNameArr,
                dimension: dimensionArr,
                sumDataId: sumDsId,
            };
            console.log("dataObj", dataObj)

            setCurPageData(prev => dataObj);
        }).catch(err => console.error(err));
    }

    // 라디오 버튼 선택 이벤트
    const onChangeSensorType = (e) => {
        e.preventDefault();
        const wdIdx = parseInt(e.target.value, 10);
        setUseQs(false);
        setChkedData([]);
        setSelSensorIdx(wdIdx);
    };
    // //admin만 전체선택 테스트용
    // const handleSelectAll = async () => {
    //     console.time("handleSelectAll");
    
    //     try {
    //         const list = dataList ?? [];
    //         if (list.length === 0) return;
    //         if (selSensorIdx < 0 || !dataStructures[selSensorIdx]) return;
    
    //         // 이미 전체선택 상태면 -> 최신 데이터 1개만 선택
    //         if (isFullListSelected(list, chkedData)){
    //             console.log("isFullListSelected", isFullListSelected(list, chkedData))
    //             const latestId = list[0].dataId;
    //             setChkedData([latestId]);
    //             setSelDataIdx(0);
    //             lastClickedIdxRef.current = 0;
    //             console.log("list길이", list.length)
    //             return;
    //         } else {
    //             console.log("isFullListSelected", isFullListSelected(list, chkedData))
    //             console.log("list길이", list.length)
    //         }
    
    //         const sumDsId = dataStructures[selSensorIdx]?.sumDsId;
    //         const isAbout = dataStructures[selSensorIdx]?.tableName;
    //         const filterBody = buildMonitFilterBody();
            
    
    //         const res = await axios.post(
    //             `${API_URL}/monitoring-manage/getAllMonitDataIds`,
    //             {
    //                 isAbout,
    //                 sumDsId,
    //                 ...filterBody,
    //             }
    //         );
    //         console.log("res.data.dataIds", res.data.dataIds)
    //         console.log("[monit-ui] getAllMonitDataIds length", Array.isArray(res.data?.dataIds) ? res.data.dataIds.length : 0);
    
    //         if (res.data?.success) {
    //             const allIds = Array.isArray(res.data.dataIds) ? res.data.dataIds : [];
    //             setChkedData(allIds);
    //             console.log("[monit-ui] chkedData(length to set)", allIds.length);
    
    //             // 전체선택 후에도 포커스는 최신 데이터 유지
    //             if (list.length > 0) {
    //                 setSelDataIdx(0);
    //                 lastClickedIdxRef.current = 0;
    //             }
    //         }
    //     } catch (err) {
    //         console.error(err);
    //     } finally {
    //         console.timeEnd("handleSelectAll");
    //     }
    // };


    // 세부데이터 삭제 버튼 이벤트
    const onDeleteData = async (e) => {
        e.preventDefault();
        const tableName = dataStructures[selSensorIdx]["tableName"];
        const dataId = curPageData["monitData"]["dataId"];
        await axios.post(`${API_URL}/monitoring-manage/deleteMonit`, { setName: tableName, dataId: dataId, noIndex: selSensorIdx, sumDsId: curPageData["dataStructure"]["sumDsId"] }).then(res => {
            const { success } = res.data;
            if (success) {
                // 현재 리스트에서 삭제된 dataId를 제외한 배열 기준으로 다음 dataId 계산
                const filtered = dataList.filter(el => el.dataId !== dataId);
                let nextDataId = null;

                if (filtered.length === 0) {
                    // 더 이상 남은 데이터가 없으면 선택 해제
                    setDataList(filtered);
                    setSelDataIdx(-1);
                    setChkedData([]);
                    lastClickedAnchorRef.current = null;
                } else {
                    // selDataIdx를 기준으로 가장 가까운 인덱스를 선택
                    const nextIdx = Math.min(selDataIdx, filtered.length - 1);
                    nextDataId = filtered[nextIdx].dataId;
                    setDataList(filtered);
                    setSelDataIdx(nextIdx);
                    setChkedData([nextDataId]);
                    syncShiftAnchorFromRow(filtered[nextIdx]);
                }

                setChkDelModalIsOpen(false);
                setToast({
                    open: true,
                    message: '세부데이터가 삭제되었습니다.',
                    nextDataId,
                });
            } else {
                setToast({
                    open: true,
                    message: '삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.',
                    nextDataId: null,
                })
            }
        }).catch(err => console.error(err));
    }
    // 최신 데이터 
    const getPrevData = async (e) => {
        try {
            const sumDsId = dataStructures[selSensorIdx]?.sumDsId;
            const res = await axios.post(`${API_URL}/monitoring-manage/getPrevMonit`, {
                headId: dataList[0].dataId,
                company,
                isAbout: dataStructures[selSensorIdx]['tableName'],
                sumDsId,
                ...buildMonitFilterBody(),
            });
            const monitDatas = res.data.monitData || [];
            if (monitDatas.length > 0) {
                setDataList((prev) => [...monitDatas, ...prev]);
                setSelDataIdx((prev) => (prev >= 0 ? prev + monitDatas.length : prev));
                setNoMoreData((prev) => res.data.end ?? prev);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const getData = useCallback(
        async (isAbout) => {
            try {
                const sumDsId = dataStructures[selSensorIdx]?.sumDsId;
                console.log('[monit-ui] getMonit payload', {
                    lastId,
                    lastSumDataId,
                    sumDsId,
                    tableName: isAbout,
                    ...buildMonitFilterBody(),
                });
                const hasRawCursor = lastId != null && lastId !== '' && Number(lastId) > 0;
                const buildPayload = (idCursor, sumCursor) => ({
                    lastId: idCursor,
                    lastSumDataId: sumCursor,
                    company,
                    isAbout,
                    sumDsId,
                    ...buildMonitFilterBody(),
                });
                const res = await axios.post(
                    `${API_URL}/monitoring-manage/getMonit`,
                    buildPayload(
                        lastId,
                        hasRawCursor ? null : lastSumDataId
                    )
                );
                const monitDatas = res.data.monitData || [];
                if (monitDatas.length > 0) {
                    setDataList((prev) => {
                        const nextList = [...prev, ...monitDatas];

                        const { sumDataId, remainingLoads } = pendingMatchRef.current;

                        if (remainingLoads > 0) {
                            const foundIdxInNewItems =
                                sumDataId != null
                                    ? monitDatas.findIndex((row) =>
                                          isSameSumDataId(row?.sumDataId, sumDataId)
                                      )
                                    : -1;

                            pendingMatchRef.current = {
                                sumDataId: null,
                                remainingLoads: 0,
                            };

                            if (foundIdxInNewItems >= 0) {
                                const nextIdx = prev.length + foundIdxInNewItems;
                                setSelDataIdx(nextIdx);
                                setChkedData([monitDatas[foundIdxInNewItems].dataId]);
                                syncShiftAnchorFromRow(monitDatas[foundIdxInNewItems]);

                                setListScrollRequest((prevScroll) => ({
                                    token: prevScroll.token + 1,
                                    mode: 'auto-match',
                                }));
                            }
                        }

                        return nextList;
                    });

                    setLastId(monitDatas[monitDatas.length - 1].dataId);
                }
                setLastSumDataId(res.data.lastSumDataId ?? null);
                setNoMoreData(res.data.end);
                console.log("추가데이터 여부", res.data)
            } catch (e) {
                console.error(e);
            }
        },
        [
            lastId,
            lastSumDataId,
            selSensorIdx,
            dataStructures,
            filterStartDate,
            filterEndDate,
            selectedModelType,
            selectedModelName,
            selectedModelNo,
            monitFilterApplied,
            appliedStartDate,
            appliedEndDate,
            appliedModelType,
            appliedModelName,
            appliedModelNo,
        ]
    );

    // CSV 저장 시도 시 CSV 형태로 변환하는 함수
    function makeCsvData() {
        console.time("makeCsvData");
        if (Object.keys(curPageData).length > 0) {
            const selData = { ...curPageData["monitData"] };
            const grpNames = dataStructures[selSensorIdx]?.indexName ?? [];
            const grpDims = dataStructures[selSensorIdx]?.indexDim ?? [];
            const defaultUnit = grpDims[0];
            const headerCols = grpNames.map((name, idx) => {
                const unit = grpDims[idx] ?? defaultUnit;
                return unit != null && String(unit).trim() !== '' ? `${name}(${unit})` : `${name}`;
            });
            const labelArr = ["time", ...headerCols];
            const dataArr = selData.data;
            const mergedArr = [labelArr, ...dataArr];
            console.timeEnd("makeCsvData");
            return mergedArr;
        }
        console.timeEnd("makeCsvData");
    }
    //전체선택 테스트용 함수 나중에 기능 추가로 진행되면 남기고 아니면 제거
    function makeSingleCsvText(raw) {
        console.time(`makeSingleCsvText_${raw?.dataId ?? "unknown"}`);
    
        try {
            const grpNames = dataStructures[selSensorIdx]?.indexName ?? [];
            const grpDims = dataStructures[selSensorIdx]?.indexDim ?? [];
            const defaultUnit = grpDims[0];
            const headerCols = grpNames.map((name, idx) => {
                const unit = grpDims[idx] ?? defaultUnit;
                return unit != null && String(unit).trim() !== '' ? `${name}(${unit})` : `${name}`;
            });
            const labelArr = ["time", ...headerCols];
            const rows = [labelArr];
    
            const rawDataStr = raw?.data ?? "";
            const rowArr = rawDataStr.split(";").filter(Boolean);
    
            rowArr.forEach((row) => {
                rows.push(row.split(","));
            });
    
            return rows.map((row) => row.join(",")).join("\n");
        } finally {
            console.timeEnd(`makeSingleCsvText_${raw?.dataId ?? "unknown"}`);
        }
    }

    function sanitizeFileName(value) {
        return String(value || '세부데이터')
            .replace(/[\\/:*?"<>|]/g, '_')
            .replace(/\s+/g, '_')
            .trim();
    }
    
    /** Raw의 date(DB time 컬럼, 15자리) 등을 측정 시각 moment로 변환 */
    function parseRawDataTimeToMoment(v) {
        if (v == null) return null;
        if (moment.isMoment(v)) return v.isValid() ? v : null;
        if (v instanceof Date) {
            const m = moment(v);
            return m.isValid() ? m : null;
        }
        const s = String(v);
        if (s.length >= 15) {
            const y = s.substr(0, 4);
            const mo = s.substr(4, 2);
            const d = s.substr(6, 2);
            const h = s.substr(9, 2);
            const mi = s.substr(11, 2);
            const sec = s.substr(13, 2);
            const m = moment.tz(
                `${y}-${mo}-${d} ${h}:${mi}:${sec}`,
                'YYYY-MM-DD HH:mm:ss',
                true,
                'Asia/Seoul'
            );
            return m.isValid() ? m : null;
        }
        if (s.length === 8 && /^\d{8}$/.test(s)) {
            const m = moment.tz(
                `${s.substr(0, 4)}-${s.substr(4, 2)}-${s.substr(6, 2)}`,
                'YYYY-MM-DD',
                true,
                'Asia/Seoul'
            );
            return m.isValid() ? m.startOf('day') : null;
        }
        const m = moment(v);
        return m.isValid() ? m : null;
    }

    function resolveExportMoment(record) {
        if (!record) return moment();
        for (const v of [record.createdAt, record.time, record.regTime, record.date]) {
            const m = parseRawDataTimeToMoment(v);
            if (m) return m;
        }
        return moment();
    }

    function makeExportBaseName(raw = null, duplicateIndex = null) {
        const sensorLabel = sanitizeFileName(
            getSensorRadioLabel(dataStructures?.[selSensorIdx]) || '세부데이터'
        );

        const m = resolveExportMoment(raw);
        const timeText = m.format('YYMMDD_HHmmss');

        // ZIP 안에서 같은 이름 겹치면 덮어쓸 수 있으니 suffix 처리
        return duplicateIndex != null
            ? `${sensorLabel}_${timeText}_${duplicateIndex}`
            : `${sensorLabel}_${timeText}`;
    }

    const handleDownloadCsv = async () => {
        console.time("handleDownloadCsv_total");
    
        try {
            const checkedIds = chkedData ?? [];
    
            if (checkedIds.length === 0) {
                alert("저장할 데이터가 없습니다.");
                return;
            }
    
            // 1건이면 CSV 저장
            if (checkedIds.length <= 1) {
                const csvData = makeCsvData();
    
                if (!csvData || csvData.length === 0) {
                    alert("저장할 데이터가 없습니다.");
                    return;
                }
    
                const csvRows = csvData.map((row) => row.join(",")).join("\n");
                const BOM = "\uFEFF";
                const blob = new Blob([BOM + csvRows], { type: "text/csv;charset=utf-8;" });
    
                const link = document.createElement("a");
                const url = URL.createObjectURL(blob);
                link.href = url;
    
                const fileName = `${makeExportBaseName(curPageData?.monitData)}.csv`;
                link.setAttribute("download", fileName);
    
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                return;
            }
    
            // 여러 건이면 ZIP 저장
            const res = await axios.post(`${API_URL}/monitoring-manage/getRawBundle`, {
                isAbout: dataStructures[selSensorIdx]?.tableName,
                dataIds: checkedIds,
            });
    
            if (!res.data?.success) {
                alert("데이터 조회 실패");
                return;
            }
    
            const rawBundle = res.data.datum ?? [];
            if (!Array.isArray(rawBundle) || rawBundle.length === 0) {
                alert("저장할 데이터가 없습니다.");
                return;
            }
    
            const zip = new JSZip();
            const usedNames = new Map();
    
            rawBundle.forEach((raw) => {
                const csvText = makeSingleCsvText(raw);
    
                let baseName = makeExportBaseName(raw);
                let finalName = `${baseName}.csv`;
    
                if (usedNames.has(finalName)) {
                    const nextIndex = usedNames.get(finalName) + 1;
                    usedNames.set(finalName, nextIndex);
                    finalName = `${makeExportBaseName(raw, nextIndex)}.csv`;
                } else {
                    usedNames.set(finalName, 0);
                }
    
                zip.file(finalName, "\uFEFF" + csvText);
            });
    
            const zipBlob = await zip.generateAsync({ type: "blob" });
            saveAs(zipBlob, `${makeExportBaseName()}.zip`);
        } catch (err) {
            console.error(err);
        } finally {
            console.timeEnd("handleDownloadCsv_total");
        }
    };
    


    // 공정 요약 → 버튼 클릭 이벤트
    const onClickChkSumData = e => {
        e.preventDefault();
        const sumDataId = curPageData?.monitData?.sumDataId;
        if (sumDataId == null || sumDataId === '') return;
        const returnParams = new URLSearchParams(location.search);
        returnParams.set('sdsId', curPageData["dataStructure"]["sumDsId"]);
        returnParams.set('sIdx', String(selSensorIdx));
        returnParams.set('dataId', curPageData.monitData.dataId);
        returnParams.set('sumDataId', sumDataId);
        navigate(
            `/home/data/sumData/detail?sumDsId=${curPageData["dataStructure"]["sumDsId"]}&dataId=${sumDataId}`,
            { state: { returnTo: `${location.pathname}?${returnParams.toString()}` } }
        );
    }

    const canOpenSumDataDetail = 
    Object.keys(curPageData).length > 0 &&
    curPageData.monitData?.sumDataId != null &&
    curPageData.monitData.sumDataId !== '';


    // selData에 변동이 있으면 input에 각 데이터를 배치
    useEffect(() => {
        // setTableLoading(true);
        const selData = Object.keys(curPageData).length > 0 && selDataIdx !== -1 ? { ...curPageData["monitData"] } : null;
        const convData = selData ? selData.data.map((el, idx) => {
            const retObj = { "id": idx };
            for (let i = 0; i < el.length; i++) {
                if (i === 0) {
                    retObj["time"] = parseFloat(el[i]);
                } else {
                    retObj[`data${i}`] = parseFloat(el[i]);
                };
            };
            return (retObj);
        }) : [];
        setFormData([...convData]);
    }, [curPageData, selSensorIdx, selDataIdx]);

    // 페이지가 처음 마운트될 때 getDds() 실행
    const initMonit = async () => {
        await getDds();
    };
    useEffect(() => {
        initMonit();
    }, [selSumDsId]);

    async function updateSelSensorIdx() {
        if (!Number.isInteger(selSensorIdx) || selSensorIdx < 0) return;
        if (!Array.isArray(dataStructures) || dataStructures.length === 0) return;
        if (selSensorIdx >= dataStructures.length) return;
        if (selectPeriod === 'currentWorks' && !isCurrentProcessStartReady) return;
        const nextTargetSumDataId =
            curPageData?.monitData?.sumDataId ??
            (Array.isArray(dataList) && selDataIdx >= 0 ? dataList[selDataIdx]?.sumDataId : null) ??
            sensorSumDataIdRef.current ??
            null;
        if (nextTargetSumDataId != null && nextTargetSumDataId !== '') {
            sensorSumDataIdRef.current = nextTargetSumDataId;
        }
        preserveSumDataIdRef.current = nextTargetSumDataId;
        pendingMatchRef.current = {
            sumDataId: nextTargetSumDataId,
            remainingLoads: nextTargetSumDataId != null ? 1 : 0,
        };
        setDataList([]);
        setChkedData([]);
        setSelDataIdx(-1);
        lastClickedAnchorRef.current = null;
        setLastId(null);
        setLastSumDataId(null);
        setNoMoreData(false);
        setIsLoading(true);
        const currentWorksStart = selectPeriod === 'currentWorks' ? getCurrentWorksStartDate() : null;
        const currentWorksEnd = currentWorksStart ? moment().toDate() : null;
        if (currentWorksStart && currentWorksEnd) {
            skipNextCurrentWorksApplyRef.current = true;
            setFilterStartDate(currentWorksStart);
            setFilterEndDate(currentWorksEnd);
            setAppliedStartDate(currentWorksStart);
            setAppliedEndDate(currentWorksEnd);
            // 센서 변경 요청에 현재공정 필터가 이미 포함된다. 같은 조건의 자동 필터
            // 요청이 뒤이어 실행되면 dataId 없이 최신 행을 다시 조회해 선택을 덮는다.
            currentWorksAppliedKeyRef.current = [
                effSumDsId ?? '',
                selSensorIdx,
                moment(currentWorksStart).toISOString(),
                moment(currentWorksEnd).toISOString(),
            ].join('|');
        }
        await getInitMonit({
            forceUseQs: useQs,
            sensorIdxOverride: selSensorIdx,
            ...(currentWorksStart && currentWorksEnd
                ? { fixedStartDate: currentWorksStart, fixedEndDate: currentWorksEnd, isFilterApplied: true }
                : {}),
        });
    }
    useEffect(() => {
        void updateSelSensorIdx();
    }, [selSensorIdx, dataStructures.length]); // selSensorIdx / 구조 준비 상태가 변경될 때 실행
    useEffect(() => {
        if (selectPeriod !== 'currentWorks') return;
        if (!isCurrentProcessStartReady) return;
        if (!Array.isArray(dataStructures) || dataStructures.length === 0) return;

        const nextSensorIdx = selSensorIdx < 0 ? 0 : selSensorIdx;
        if (nextSensorIdx >= dataStructures.length) return;

        const start = getCurrentWorksStartDate();
        const startReady = Math.abs(moment(filterStartDate).diff(start, 'seconds')) <= 1;
        if (!startReady) return;
        if (skipNextCurrentWorksApplyRef.current) {
            skipNextCurrentWorksApplyRef.current = false;
            return;
        }

        const appliedKey = [
            effSumDsId ?? '',
            nextSensorIdx,
            moment(start).toISOString(),
            moment(filterEndDate).toISOString(),
        ].join('|');

        if (currentWorksAppliedKeyRef.current === appliedKey) return;
        currentWorksAppliedKeyRef.current = appliedKey;

        void applyMonitoringFilter({
            startDate: start,
            endDate: filterEndDate,
            sensorIdx: nextSensorIdx,
            resetSelectedData: true,
        });
    }, [selectPeriod, currentProcessStartedAt, isCurrentProcessStartReady, dataStructures.length, selSensorIdx, effSumDsId, filterStartDate, filterEndDate, getCurrentWorksStartDate]);

    useEffect(() => {
        if (selDataIdx >= 0 && md.length > 0) {
            getMonitData();
        } else {
            setCurPageData(false);
            setFormData(false);
        }
    }, [selSensorIdx, selDataIdx, md.length]);

    // 좌측 메뉴로 파라미터 없이 진입해도 최초 선택 행을 URL에 고정한다.
    // 이후 새 데이터가 저장되거나 이 화면을 다시 열어도 API가 최신 행으로 이동하지 않는다.
    useEffect(() => {
        const selectedRow = dataList[selDataIdx];
        const selectedStructure = dataStructures[selSensorIdx];
        if (!selectedRow || !selectedStructure) return;

        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sdsId', String(selectedStructure.sumDsId));
        nextParams.set('sIdx', String(selSensorIdx));
        nextParams.set('dataId', String(selectedRow.dataId));
        if (selectedRow.sumDataId != null && selectedRow.sumDataId !== '') {
            nextParams.set('sumDataId', String(selectedRow.sumDataId));
        } else {
            nextParams.delete('sumDataId');
        }
        const selectedStartDate = monitFilterApplied ? appliedStartDate : filterStartDate;
        const selectedEndDate = monitFilterApplied ? appliedEndDate : filterEndDate;
        if (moment(selectedStartDate).isValid() && moment(selectedEndDate).isValid()) {
            nextParams.set('filterStartDate', moment(selectedStartDate).toISOString());
            nextParams.set('filterEndDate', moment(selectedEndDate).toISOString());
        }

        if (nextParams.toString() !== searchParams.toString()) {
            setSearchParams(nextParams, { replace: true });
        }
    }, [selSensorIdx, selDataIdx, dataList, dataStructures, searchParams, setSearchParams, monitFilterApplied, appliedStartDate, appliedEndDate, filterStartDate, filterEndDate]);

    const applyMonitoringFilter = async ({
        startDate = filterStartDate,
        endDate = filterEndDate,
        sensorIdx = selSensorIdx < 0 ? 0 : selSensorIdx,
        resetSelectedData = false,
    } = {}) => {
        setUseQs(false);
        preserveSumDataIdRef.current = null;
        pendingMatchRef.current = { sumDataId: null, remainingLoads: 0 };

        if (selSensorIdx < 0) setSelSensorIdx(sensorIdx);
        setIsLoading(true);
        setDataList([]);
        if (resetSelectedData) setSelDataIdx(-1);
        setChkedData([]);
        setLastId(null);
        setLastSumDataId(null);
        setNoMoreData(false);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setAppliedModelType(selectedModelType);
        setAppliedModelName(selectedModelName);
        setAppliedModelNo(selectedModelNo);
        setMonitFilterApplied(true);
        await getInitMonit({
            forceUseQs: false,
            sensorIdxOverride: sensorIdx,
            isFilterApplied: true,
            fixedStartDate: startDate,
            fixedEndDate: endDate,
        });
    };

    const handleFilterApply = async (e) => {
        e.preventDefault();
        await applyMonitoringFilter();
    };

    const handleResetPeriod = async (e) => {
        e.preventDefault();
        setSelectPeriod('currentWorks');
        preserveSumDataIdRef.current = null;
        pendingMatchRef.current = { sumDataId: null, remainingLoads: 0 };
        const start = getCurrentWorksStartDate();
        const end = moment().toDate();
        setFilterStartDate(start);
        setFilterEndDate(end);
        setAppliedStartDate(start);
        setAppliedEndDate(end);
        setUseQs(false);
        const nextSensorIdx = selSensorIdx < 0 ? 0 : selSensorIdx;
        if (selSensorIdx < 0) setSelSensorIdx(0);
        setIsLoading(true);
        setDataList([]);
        setSelDataIdx(-1);
        setChkedData([]);
        setLastId(null);
        setLastSumDataId(null);
        setNoMoreData(false);
        setMonitFilterApplied(true);
        await getInitMonit({ fixedStartDate: start, fixedEndDate: end, forceUseQs: false, sensorIdxOverride: nextSensorIdx, isFilterApplied: true });
    };

    const onRefreshHeader = async (e) => {
        e.preventDefault();
        preserveSumDataIdRef.current = null;
        pendingMatchRef.current = { sumDataId: null, remainingLoads: 0 };

        const nowEnd = selectPeriod === 'currentWorks' ? moment().toDate() : null;
        if (nowEnd) {
            setFilterEndDate(nowEnd);
            setAppliedStartDate(filterStartDate);
            setAppliedEndDate(nowEnd);
        }
        const nextSensorIdx = selSensorIdx < 0 ? 0 : selSensorIdx;
        if (selSensorIdx < 0) setSelSensorIdx(0);
        setIsLoading(true);
        setDataList([]);
        setSelDataIdx(-1);
        setChkedData([]);
        setLastId(null);
        setLastSumDataId(null);
        setNoMoreData(false);
        setMonitFilterApplied(selectPeriod === 'currentWorks');
        await getInitMonit(nowEnd ? { fixedEndDate: nowEnd, fixedStartDate: filterStartDate, forceUseQs: false, sensorIdxOverride: nextSensorIdx, isFilterApplied: true } : { forceUseQs: false, sensorIdxOverride: nextSensorIdx });
    };

    return isLoading ? (
        <Loading />
    ) : (
        <React.Fragment>
            <div className="monitoringPage monitPageV2">
                <div className="monitPageHeaderRow monitPageHeaderRow--titlesOnly">
                    <div>
                        <p className="path">
                            데이터 관리<span className="below">{'>'}</span>세부 데이터
                        </p>
                        <h2 className="pageTitle">세부 데이터</h2>
                    </div>
                </div>

                <div className="sumDataStatistic monitFilterScope">
                    <div className="filterCard">
                        <form
                            onSubmit={handleFilterApply}
                            onKeyDown={(ev) => {
                                if (ev.key === 'Enter') ev.preventDefault();
                            }}
                        >
                            <div className="filterRow filterRow1">
                                <div className="timeFilterArea">

                                <span className="datePickerLabel">시간</span>
                                <div className="datePickerArea">
                                    <div className="startDateArea">
                                        <CustomDatePickerStart
                                            startDate={filterStartDate}
                                            setStartDate={setFilterStartDate}
                                            endDate={filterEndDate}
                                            changeDropdown={() => setSelectPeriod('input')}
                                        />
                                    </div>
                                    <span>-</span>
                                    <div className="endDateArea">
                                        <CustomDatePickerEnd
                                            startDate={filterStartDate}
                                            endDate={filterEndDate}
                                            setEndDate={setFilterEndDate}
                                            changeDropdown={() => setSelectPeriod('input')}
                                        />
                                    </div>
                                </div>
                                <Select
                                    options={periodOpt}
                                    value={periodOpt.find((el) => el.value === selectPeriod)}
                                    onChange={(sel) => setSelectPeriod(sel.value)}
                                    isSearchable={false}
                                    className="filterSelect periodSelect periodOnly"
                                    classNames={{
                                        control: (state) => (state.isFocused ? 'selectControl focused' : 'selectControl'),
                                        container: () => 'selectBox',
                                        menu: () => 'selectMenu',
                                        menuList: () => 'optionsList',
                                        option: (s) => (s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option'),
                                        singleValue: () => 'singleVal',
                                        valueContainer: () => 'valueContainer',
                                    }}
                                    components={{
                                        DropdownIndicator: (props) => (
                                            <components.DropdownIndicator {...props}>
                                                <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                    <img src={filledArrowIconImg} alt="드롭다운" />
                                                </span>
                                            </components.DropdownIndicator>
                                        ),
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                                <div className="filterRow1Right">
                                    <button type="submit" className="applyBtn">
                                        적용
                                    </button>
                                    <button type="button" className="resetPeriodBtn" onClick={handleResetPeriod}>
                                        <span>기간 초기화</span>
                                        <img src={resetIconImg} alt="초기화" />
                                    </button>
                                </div>
                                </div>
                                <button
                                    type="button"
                                    className="monitHeaderRefreshBtn monitHeaderRefreshBtn--round monitFilterRow1Refresh"
                                    onClick={onRefreshHeader}
                                    aria-label="새로고침"
                                >
                                    <img
                                        className="monitRefreshBtnImg"
                                        src={refreshIcon}
                                        alt=""
                                        draggable={false}
                                    />
                                </button>
                            </div>
                            <div className="filterRow filterRow2 monitFilterRow2">
                                <span className="filterLabel">품목</span>
                                <div className="selectGroup">
                                    <div className="selectItem">
                                        <span className="selectItemLabel">차종</span>
                                        <Select
                                            options={modelTypeOptions}
                                            value={selectedModelType}
                                            onChange={(v) => {
                                                setSelectedModelType(v);
                                                setSelectedModelName(OPTION_ALL);
                                                setSelectedModelNo(OPTION_ALL);
                                            }}
                                            placeholder="전체 선택"
                                            isSearchable={false}
                                            classNames={{
                                                control: () => 'selectControl',
                                                container: () => 'selectBox',
                                                menu: () => 'selectMenu',
                                                option: (s) => (s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option'),
                                                singleValue: () => 'singleVal',
                                                valueContainer: () => 'valueContainer',
                                                placeholder: () => 'selectPlaceholder',
                                            }}
                                            components={{
                                                DropdownIndicator: (s) => (
                                                    <span className={s.isFocused ? 'arrowIcon focused' : 'arrowIcon'}>
                                                        <img src={filledArrowIconImg} alt="드롭다운" />
                                                    </span>
                                                ),
                                                IndicatorSeparator: () => null,
                                            }}
                                        />
                                    </div>
                                    <div className="selectItem">
                                        <span className="selectItemLabel">품명</span>
                                        <Select
                                            options={modelNameOptions}
                                            value={selectedModelName}
                                            onChange={(v) => {
                                                setSelectedModelName(v);
                                                setSelectedModelNo(OPTION_ALL);
                                            }}
                                            placeholder="전체 선택"
                                            isSearchable={false}
                                            isDisabled={!selectedModelType || selectedModelType.value === ''}
                                            classNames={{
                                                control: (state) =>
                                                    state.isDisabled ? 'selectControl selectControl--disabled' : 'selectControl',
                                                container: () => 'selectBox',
                                                menu: () => 'selectMenu',
                                                option: (s) => (s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option'),
                                                singleValue: () => 'singleVal',
                                                valueContainer: () => 'valueContainer',
                                                placeholder: () => 'selectPlaceholder',
                                            }}
                                            components={{
                                                DropdownIndicator: (props) => (
                                                    <components.DropdownIndicator {...props}>
                                                        <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                            <img src={filledArrowIconImg} alt="드롭다운" />
                                                        </span>
                                                    </components.DropdownIndicator>
                                                ),
                                                IndicatorSeparator: () => null,
                                            }}
                                        />
                                    </div>
                                    <div className="selectItem">
                                        <span className="selectItemLabel">품번</span>
                                        <Select
                                            options={modelNoOptions}
                                            value={selectedModelNo}
                                            onChange={(v) => setSelectedModelNo(v)}
                                            placeholder="전체 선택"
                                            isSearchable={false}
                                            isDisabled={!selectedModelName || selectedModelName.value === ''}
                                            classNames={{
                                                control: (state) =>
                                                    state.isDisabled ? 'selectControl selectControl--disabled' : 'selectControl',
                                                container: () => 'selectBox',
                                                menu: () => 'selectMenu',
                                                menuList: () => 'optionsList',
                                                option: (s) => (s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option'),
                                                singleValue: () => 'singleVal',
                                                valueContainer: () => 'valueContainer',
                                                placeholder: () => 'selectPlaceholder',
                                            }}
                                            components={{
                                                DropdownIndicator: (props) => (
                                                    <components.DropdownIndicator {...props}>
                                                        <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                            <img src={filledArrowIconImg} alt="드롭다운" />
                                                        </span>
                                                    </components.DropdownIndicator>
                                                ),
                                                IndicatorSeparator: () => null,
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="monitFilterRow2Actions">
                                    <button type="button" className="saveCSVBtn" onClick={handleDownloadCsv}>
                                        <img src={csvSaveIcon} alt="csv 저장" />
                                        <span>CSV 저장</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="deleteDataBtn monitFilterDeleteBtn"
                                        onClick={(ev) => {
                                            ev.preventDefault();
                                            setChkDelModalIsOpen(true);
                                        }}
                                        disabled={!privilege?.detailedData?.delete}
                                    >
                                        삭제
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="monitArea monitAreaV2">
                    <DataListMenu
                        dDs={dataStructures[selSensorIdx]}
                        monitData={selSensorIdx >= 0 ? dataList : []}
                        selSensorIdx={selSensorIdx}
                        selDataIdx={selDataIdx}
                        setSelDataIdx={setSelDataIdx}
                        chkedData={chkedData}
                        onRowClick={handleRowClick}
                        setChkedData={setChkedData}
                        listScrollRequest={listScrollRequest}
                        getPrevData={(e) => {
                            e.preventDefault();
                            getPrevData(e);
                        }}
                        getMoreData={(e) => {
                            e.preventDefault();
                            getData(dataStructures[selSensorIdx]['tableName']);
                        }}
                        isLastData={noMoreData}
                    />
                    <div className="monitPrintArea">
                        <div className="monitSensorBar">
                            <div
                                className="monitSensorBarInner"
                                style={{ minWidth: `${Math.max(200 * dataStructures.length, 100)}px` }}
                            >
                                {dataStructures.map((item, idx) => {
                                    const valView = item['valView'];
                                    if (valView.filter((el) => el === true).length === 0) {
                                        return null;
                                    }
                                    const radioId = `sensor_radio_${item.dDsId ?? idx}`;
                                    return (
                                        <div className="sensorOnOffArea" key={radioId}>
                                            <p>
                                                <input
                                                    className="sensorTypeRadio"
                                                    type="radio"
                                                    name="sensorType"
                                                    id={radioId}
                                                    value={idx}
                                                    onChange={onChangeSensorType}
                                                    checked={selSensorIdx === idx}
                                                />
                                                <label htmlFor={radioId} className="sensorTitle">
                                                    <span
                                                        className={selSensorIdx === idx ? 'radioVisual on' : 'radioVisual off'}
                                                    />
                                                    {getSensorRadioLabel(item)}
                                                </label>
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {Object.keys(curPageData).length > 0 ? (
                            <div className="monitContainer monitContainer--noTable">
                                <div className="monitTblDiv monitTblDiv--noTable">
                                    <div className="monitTblTop monitTblTop--summaryOnly">
                                        <div className="sumDataInfoDiv sumDataInfoDiv--large">
                                            <SumDataInfo
                                                curPageMonitData={curPageData['monitData']}
                                                sumDsId={
                                                    Object.keys(curPageData).length > 0
                                                        ? curPageData['dataStructure']['sumDsId']
                                                        : -1
                                                }
                                            />
                                            <button
                                                type="button"
                                                className="toSumData"
                                                disabled={!canOpenSumDataDetail}
                                                onClick={onClickChkSumData}
                                            >
                                                <span>공정 요약</span>
                                                <span className="icon">→</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="chartArea">
                                    {formData === false ? (
                                        <NoData />
                                    ) : (
                                        <div className="lineChartArea">
                                            <CustomLineChart data={formData} dsData={curPageData.dataStructure} />
                                            <ChartInfo
                                                data={{ ...curPageData['monitData'], data: formData }}
                                                dataStructure={curPageData.dataStructure}
                                            />
                                            
                                            <div className="legendDiv">
                                                {curPageData.dataStructure.indexName.map((idxName, idx) => {
                                                    if (curPageData.dataStructure.valView[idx]) {
                                                        return (
                                                            <div className="legend" key={`legend_${idx}`}>
                                                                <div
                                                                    className="stroke"
                                                                    style={{ backgroundColor: idx === 0 ? '#0A2B83' : '#45BFCE' }}
                                                                />
                                                                <p>{idxName}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : curPageData === false ? (
                            <div className="chartArea">
                                <NoData />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
                    <ChkDelModal
                        isOpen={chkDelModalIsOpen}
                        onRequestClose={() => setChkDelModalIsOpen(false)}
                        onSubmit={onDeleteData}
                    />
                    <FadeOutNoti
                        showNoti={toast.open}
                        onClose={() => {
                            if (toast.nextDataId) {
                                navigate(
                                    {
                                        pathname: '.',
                                        search: `?sdsId=${qsSdsId}&sIdx=${selSensorIdx}&dataId=${toast.nextDataId}`,
                                    },
                                    { replace: true }
                                );
                            }
                            setToast({ open: false, message: '', nextDataId: null });
                        }}
                        msg={toast.message}
                        bg={false}
                    />
        </React.Fragment>
    );
}

function SumDataInfo({ curPageMonitData, sumDsId }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        setData(null);
        async function fetchData() {
            try {
                const response = await axios.post(`${API_URL}/monitoring-manage/getSumData`, { sumDsId: sumDsId, dataId: curPageMonitData["sumDataId"] });
                setData(response.data);
                console.log('sumDsId:', sumDsId);
                console.log('sumDataId:', curPageMonitData?.sumDataId);
            } catch (err) {
                console.error(err);
            }
        }

        fetchData();
    }, [curPageMonitData, sumDsId]);

    if (!data) {
        return <div>로딩 중...</div>;
    }
    // 데이터 없으면 밑에 세 상수 null처리 
    if (data.data === null) {
        const dash = '-';
        return (
            <div className="sumDataInfoArea sumDataInfoArea--extended sumDataInfoChartCard">
                <p className="sumDataInfoLine">
                    <span className="sumDataInfoLabel">카운트</span>
                    <span className="sumDataInfoSep">:</span>
                    <span className="sumDataInfoVal">{curPageMonitData['serialNo'] ?? dash}</span>
                </p>
                <p className="sumDataInfoLine">
                    <span className="sumDataInfoLabel">차종</span>
                    <span className="sumDataInfoSep">:</span>
                    <span className="sumDataInfoVal">{dash}</span>
                </p>
                <p className="sumDataInfoLine">
                    <span className="sumDataInfoLabel">품명</span>
                    <span className="sumDataInfoSep">:</span>
                    <span className="sumDataInfoVal">{dash}</span>
                </p>
                <p className="sumDataInfoLine">
                    <span className="sumDataInfoLabel">품번</span>
                    <span className="sumDataInfoSep">:</span>
                    <span className="sumDataInfoVal">{dash}</span>
                </p>
                <p className="sumDataInfoLine">
                    <span className="sumDataInfoLabel">판정</span>
                    <span className="sumDataInfoSep">:</span>
                    <span className="sumDataInfoVal">{dash}</span>
                </p>
                <p className="sumDataInfoLine">
                    <span className="sumDataInfoLabel">코멘트</span>
                    <span className="sumDataInfoSep">:</span>
                    <span className="sumDataInfoVal">{dash}</span>
                </p>
            </div>
        );
    }
    const row = data.data;
    const modelType = row.model?.modelType;
    const modelName = row.model?.modelName;
    const modelNo = row.model?.modelNo;
    const decision = row.decision;
    const comment = row.comment;

    const fmt = (v) => {
        if (v === null || v === undefined) return '-';
        const s = String(v).trim();
        if (s === '' || s === '　-　') return '-';
        return String(v);
    };

    return (
        <div className="sumDataInfoArea sumDataInfoArea--extended sumDataInfoChartCard">
            <p className="sumDataInfoLine">
                <span className="sumDataInfoLabel">카운트</span>
                <span className="sumDataInfoSep">:</span>
                <span className="sumDataInfoVal">{fmt(curPageMonitData['serialNo'])}</span>
            </p>
            <p className="sumDataInfoLine">
                <span className="sumDataInfoLabel">차종</span>
                <span className="sumDataInfoSep">:</span>
                <span className="sumDataInfoVal">{fmt(modelType)}</span>
            </p>
            <p className="sumDataInfoLine">
                <span className="sumDataInfoLabel">품명</span>
                <span className="sumDataInfoSep">:</span>
                <span className="sumDataInfoVal">{fmt(modelName)}</span>
            </p>
            <p className="sumDataInfoLine">
                <span className="sumDataInfoLabel">품번</span>
                <span className="sumDataInfoSep">:</span>
                <span className="sumDataInfoVal">{fmt(modelNo)}</span>
            </p>
            <p className="sumDataInfoLine">
                <span className="sumDataInfoLabel">판정</span>
                <span className="sumDataInfoSep">:</span>
                <span className="sumDataInfoVal">{fmt(decision)}</span>
            </p>
            <p className="sumDataInfoLine">
                <span className="sumDataInfoLabel">코멘트</span>
                <span className="sumDataInfoSep">:</span>
                <span className="sumDataInfoVal">{fmt(comment)}</span>
            </p>
        </div>
    );
}

function ChartInfo({ data, dataStructure }) {
    const convertedData = data.data;
    const data1Arr = convertedData.map(el => parseFloat(el.data1));
    const data2Arr = convertedData.map(el => parseFloat(el.data2 ?? 0));
    const max1 = Math.max(...data1Arr);
    const max2 = Math.max(...data2Arr);
    const maxArr = [max1, max2];
    let parsedLabel = null;
    if (data.label) {
        if (typeof data.label === 'string') {
            try {
                parsedLabel = JSON.parse(data.label);
            } catch (e) {
                parsedLabel = data.label.split(/[|,]/).reduce((acc, pair) => {
                    const [key, rawValue] = pair.split(':');
                    if (key) acc[key] = rawValue;
                    return acc;
                }, {});
            }
        } else if (typeof data.label === 'object') {
            parsedLabel = data.label;
        }
    }
    const dateData = data.date;
    const year = parseInt(dateData.substr(0, 4));
    const month = parseInt(dateData.substr(4, 2));
    const date = parseInt(dateData.substr(6, 2));
    const hours = parseInt(dateData.substr(9, 2));
    const minutes = parseInt(dateData.substr(11, 2));
    const seconds = parseInt(dateData.substr(13, 2));
    const ampm = hours < 12 ? '오전' : '오후';

    if (parsedLabel) {
        const labelOrder = ["X", "Y", "좌", "우", "피크"];
        const labelEntries = labelOrder
            .filter(key => Object.prototype.hasOwnProperty.call(parsedLabel, key))
            .map(key => [key, parsedLabel[key]]);
        return (
            <div className='chartInfoArea'>
                <p>
                    <span>
                        {"생산 일시 : "}
                    </span>
                    <span>
                        {`${year}년 ${month}월 ${date}일 ${ampm} ${hours > 12 ? hours - 12 : hours}시 ${minutes}분 ${seconds}초`}
                    </span>
                </p>
                {
                    labelEntries.map(([key, value]) => {
                        const numericValue = Number(value);
                        const displayValue = Number.isFinite(numericValue)
                            ? numericValue.toFixed(2)
                            : (value ?? '-');
                        return (
                            <p key={`chartInfo_label_${key}`}>
                                <span>
                                    {`${key} : `}
                                </span>
                                <span>
                                    {displayValue}
                                </span>
                            </p>
                        );
                    })
                }
            </div>
        );
    }
    if (dataStructure.class !== "하중") {
        return (<div className='chartInfoArea'>
            <p>
                <span>
                    {"생산 일시 : "}
                </span>
                <span>
                    {`${year}년 ${month}월 ${date}일 ${ampm} ${hours > 12 ? hours - 12 : hours}시 ${minutes}분 ${seconds}초`}
                </span>
            </p>
            {
                dataStructure.indexName.map((idxName, idx) => {
                    if (dataStructure.valView[idx]) {
                        return (
                            <p key={`chartInfo_${idx}`}>
                                <span>
                                    {`${idxName} : `}
                                </span>
                                <span>
                                    {maxArr[idx].toFixed(2)}
                                </span>
                            </p>
                        );
                    }
                })
            }
            {/* <p>
                <span>
                    {`${dataStructure.indexName[0]} : `}
                </span>
                <span>
                    {max1.toFixed(2)}
                </span>
            </p>
            <p>
                <span>
                    {`${dataStructure.indexName[1]} : `}
                </span>
                <span>
                    {max2.toFixed(2)}
                </span>
            </p> */}
        </div>);
    } else {
        return (<div className='chartInfoArea'>
            <p>
                <span>
                    {"생산 일시 : "}
                </span>
                <span>
                    {`${year}년 ${month}월 ${date}일 ${ampm} ${hours > 12 ? hours - 12 : hours}시 ${minutes}분 ${seconds}초`}
                </span>
            </p>
            <p>
                <span>
                    {`${dataStructure.indexName[0]} : `}
                </span>
                <span>
                    {max1.toFixed(2)}
                </span>
            </p>
            {/* <p>
                <span>
                    {`이상패턴 : `}
                </span>
                <span>
                    {data["sumData"][`data_${}`] ?? '-'}
                </span>
            </p> */}
        </div>);
    }

}

export default RealTimeMonitoring;
